import type { GetSpotlightGrants } from 'src/openapi';
import {
  PRO_GRANT_AI_CREDITS,
  PRO_PLAN,
  SPOTLIGHT_SCOPE_NOTE,
} from 'src/components/buyCreditsDialog/benefits';

// Unprefixed perk keys, matching SPOTLIGHT_PERKS in the Spotlight dialog. The
// live grant counts arrive from /constants/values keyed WITH a `spotlight_`
// prefix (GetSpotlightGrants), so readers prepend it — see FeaturedProductCard.
export type SpotlightCountKey =
  | 'profile_highlight'
  | 'recruiter_dm'
  | 'recruiter_email'
  | 'ai_message_assistance'
  | 'smart_job_alerts';

// A benefit is either plain copy, or a template whose `{count}` placeholder is
// filled from the live grant so the card advertises the same numbers the perks
// modal shows. Templates must read correctly with the placeholder removed too —
// that's the fallback when the constant hasn't loaded yet.
export type FeaturedBenefit =
  | string
  | { template: string; countKey: SpotlightCountKey };

// Shared by the card and the perks modal so the two can never advertise
// different numbers. When the constant hasn't loaded the placeholder is dropped
// along with its trailing space rather than rendering "-", so the sentence still
// reads correctly and the card never advertises a number it doesn't have.
export const featuredBenefitText = (
  benefit: FeaturedBenefit,
  spotlightConstants?: GetSpotlightGrants,
): string => {
  if (typeof benefit === 'string') return benefit;
  const count = spotlightConstants?.[`spotlight_${benefit.countKey}`];
  return count
    ? benefit.template.replace('{count}', String(count))
    : benefit.template.replace('{count} ', '');
};

export type FeaturedHighlight = {
  badge?: string;
  tagline: string;
  benefits: FeaturedBenefit[];
  /** Ground for the hero panel, behind the product artwork. */
  heroGradient?: string;
  /**
   * Whether `heroGradient` is a deep ground. Drives the panel's own bloom, its
   * border and the light-on-dark variants of the badge and ribbon — none of
   * which can be inferred from a gradient string.
   */
  heroOnDark?: boolean;
  /** Product artwork that replaces the catalogue image on the hero card. */
  artwork?: 'proPlusAbacus';
  /** One line under the perks: what the membership is worth in total. */
  valueLine?: string;
  cta: string;
  // Short reassurance shown under the price in the detail modal (e.g. billing).
  valueNote?: string;
  // Caveat shown under the benefits list — a scope limit the buyer needs before
  // paying, not a selling point.
  footnote?: string;
  // Accent gradient used for the card's header band, badge and CTA glow.
  accentGradient: string;
  // Fill for the CTA button when it should not carry the accent gradient (the
  // amber gradient's dark end read as muddy behind the white label).
  ctaBackground?: string;
  glowColor: string;
};

// Solid amber for the Pro CTA — the storefront card's button and the checkout
// dialog's, so the two read as the same key.
export const PRO_CTA_COLOR = '#F59E0B';

// The deep navy every Pro+ panel sits on — the storefront card and the
// purchase-success page — so the plan bought and the plan landed on read as
// one product. Navy, not amber: the gold mark is the subject, and gold on gold
// reads as a flat sticker.
export const PRO_HERO_GRADIENT =
  'linear-gradient(158deg, #123159 0%, #16396A 38%, #0D2244 74%, #081830 100%)';

// Defined beside the Pro plan (which lists Spotlight with this caveat) and
// re-exported so the Spotlight surfaces keep importing it from the store.
export { SPOTLIGHT_SCOPE_NOTE };

// Benefit copy is grounded in real product features (see abekus-products skill):
// Spotlight+ = recruiter-bypass advantage; Premium (Standard plan) = 2500 AI
// credits + core job-hunt features free for 30 days. Spotlight counts are never
// hard-coded — they carry a countKey and are resolved live from
// /constants/values, since the grants change per offer.
export const FEATURED_PRODUCT_HIGHLIGHTS: Record<string, FeaturedHighlight> = {
  applicant_spotlight: {
    badge: 'POPULAR WITH JOB SEEKERS',
    cta: 'Get Spotlight+',
    tagline: 'Jump the line — get seen by recruiters first.',
    footnote: SPOTLIGHT_SCOPE_NOTE,
    benefits: [
      {
        template:
          'Get {count} highlights for priority visibility in recruiter searches',
        countKey: 'profile_highlight',
      },
      {
        template:
          'Send {count} direct messages to skip recruiter screening queues',
        countKey: 'recruiter_dm',
      },
      {
        template: 'Unlock {count} verified recruiter emails directly',
        countKey: 'recruiter_email',
      },
      {
        template:
          'Use {count} AI assists for high-converting outreach messages',
        countKey: 'ai_message_assistance',
      },
      {
        template: 'Receive {count} early job alerts on WhatsApp & email',
        countKey: 'smart_job_alerts',
      },
    ],
    accentGradient:
      'linear-gradient(135deg, #6D28D9 0%, #2563EB 55%, #06B6D4 100%)',
    glowColor: 'rgba(109, 40, 217, 0.45)',
  },
  // The current premium offering — Abekus Pro+ (Standard plan). Courses lead as
  // the headline benefit (they are paid otherwise, so this is the biggest save).
  premium_profile_standard: {
    badge: 'BEST VALUE',
    cta: 'Go Pro+',
    // Courses are waived outright; the AI credits are a grant, so they are
    // "+ 2,500", never "free".
    tagline: `Every paid course + ${PRO_GRANT_AI_CREDITS.toLocaleString('en-IN')} AI credits — your best-value month.`,
    valueNote: 'One-time payment · valid 30 days',
    // A cream panel has no dark to glow against, and turns into the brightest
    // block on the page in dark mode.
    heroGradient: PRO_HERO_GRADIENT,
    heroOnDark: true,
    artwork: 'proPlusAbacus',
    // Not "everything you pay for": AI features stay metered, and only one
    // guidance call a month is waived.
    valueLine: `All ${PRO_PLAN.length} benefits in one plan`,
    // The whole plan, from the one list every Pro surface reads.
    benefits: PRO_PLAN.map((item) => item.long),
    accentGradient:
      'linear-gradient(135deg, #B45309 0%, #F59E0B 50%, #FBBF24 100%)',
    ctaBackground: PRO_CTA_COLOR,
    glowColor: 'rgba(245, 158, 11, 0.45)',
  },
  // Legacy unlimited plan. Kept so copy still resolves if an existing buyer's
  // item is ever surfaced (e.g. order history), but intentionally excluded from
  // FEATURED_ITEM_TYPES so it is no longer sold as a storefront hero card.
  premium_profile: {
    badge: 'BEST VALUE',
    cta: 'Go Premium',
    tagline: 'Go all-in — unlimited credits for a full month.',
    valueNote: 'One-time payment · valid 30 days',
    benefits: [
      'Unlimited job applications',
      'Unlimited Skill Builder practice',
      'Unlimited resume builds & rewrites',
      'No credit limits anywhere for 30 full days',
    ],
    accentGradient:
      'linear-gradient(135deg, #B45309 0%, #F59E0B 50%, #FBBF24 100%)',
    ctaBackground: PRO_CTA_COLOR,
    glowColor: 'rgba(245, 158, 11, 0.45)',
  },
};

// The storefront features the new Standard plan (not the legacy unlimited item).
// Abekus Pro+ is listed first — it renders as the full-width hero in AbekusShop.
//
// `applicant_spotlight` is deliberately absent: web sells Spotlight for credits
// through the Spotlight dialog, not as a cash storefront product. Its copy stays
// in FEATURED_PRODUCT_HIGHLIGHTS above so anything rendering a past order still
// resolves, and so it comes straight back if the card is ever wanted again.
export const FEATURED_ITEM_TYPES = ['premium_profile_standard'];

export const isFeaturedItem = (itemType?: string | null): boolean =>
  !!itemType && FEATURED_ITEM_TYPES.includes(itemType);
